import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import FleetService from "@/services/FleetService";
export interface DriverFormData {
  address: string;
  email: string;
  id: string;
  licenseExpiry: string;
  licenseNumber: string;
  name: string;
  phone: string;
  profilePicture?: File;
  rating: number;
  status: "active" | "inactive" | "suspended";
  vehicle: string;
}

interface EditDriverModalProps {
  initialData: DriverFormData;
  onClose: () => void;
  onSubmit: (data: DriverFormData) => void;
  open: boolean;
}

interface Vehicle {
  assignedDriver?: string;
  brand: string;
  id: string;
  model: string;
  plate: string;
}

const InputField = ({
  className,
  id,
  inputProps,
  label,
  onChange,
  type = "text",
  value,
}: {
  className?: string;
  id: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  label: string;
  onChange: (value: string) => void;
  type?: string;
  value: string;
}) => (
  <div className={`relative ${className || ""}`}>
    <input
      className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
      id={id}
      onChange={(e) => onChange(e.target.value)}
      required
      type={type}
      value={value}
      {...(inputProps || {})}
    />
    <label
      className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
      htmlFor={id}
    >
      {label}
    </label>
  </div>
);

const EditDriverModal: React.FC<EditDriverModalProps> = ({
  initialData,
  onClose,
  onSubmit,
  open,
}) => {
  const [form, setForm] = useState<DriverFormData>(initialData);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  // Load vehicles when modal opens
  useEffect(() => {
    if (open) {
      loadVehicles();
    }
  }, [open]);

  // Update form when initialData changes
  useEffect(() => {
    setForm(initialData);
  }, [initialData]);

  const loadVehicles = async () => {
    try {
      setLoadingVehicles(true);
      const response = await FleetService.getVehicles();
      let availableVehicles: Vehicle[] = [];
      if (Array.isArray(response)) {
        availableVehicles = response
          .filter(
            (v: any) =>
              !v.assigned_driver || !v.assignedDriver || v.id === form.vehicle,
          )
          .map((v: any) => ({
            assignedDriver: v.assigned_driver || v.assignedDriver,
            brand: v.brand || v.make || "",
            id: v.id?.toString() || "",
            model: v.model || "",
            plate: v.plate_number || v.plate || v.licensePlate || "",
          }));

        // If the assigned vehicle is not in the list, fetch and add it
        if (
          form.vehicle &&
          !availableVehicles.some((v) => v.id === form.vehicle)
        ) {
          try {
            const assignedVehicle = await FleetService.getVehicle(form.vehicle);
            if (assignedVehicle && assignedVehicle.id) {
              availableVehicles.push({
                assignedDriver:
                  assignedVehicle.assigned_driver ||
                  assignedVehicle.assignedDriver,
                brand: assignedVehicle.brand || assignedVehicle.make || "",
                id: assignedVehicle.id?.toString() || "",
                model: assignedVehicle.model || "",
                plate:
                  assignedVehicle.plate_number ||
                  assignedVehicle.plate ||
                  assignedVehicle.licensePlate ||
                  "",
              });
            }
          } catch (err) {
            // Ignore fetch error for assigned vehicle
          }
        }
        setVehicles(availableVehicles);
      }
    } catch (error) {
      console.error("Error loading vehicles:", error);
    } finally {
      setLoadingVehicles(false);
    }
  };

  if (!open) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setForm({ ...form, profilePicture: e.target.files[0] });
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg dark:bg-gray-900 md:max-w-3xl lg:max-w-4xl xl:max-w-5xl 2xl:max-w-6xl"
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "90vh", overflowY: "auto", width: "80vw" }}
      >
        <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          Edit Driver
        </h2>
        <form
          className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              const { vehicle, ...rest } = form;
              const payload = {
                ...rest,
                assigned_vehicle_id: vehicle,
              };
              await FleetService.updateDriver(form.id, payload); // Call backend API here
              toast.success("Driver details saved successfully!");
              onSubmit(form); // Optionally update local state/UI
            } catch (err) {
              toast.error("Failed to update driver");
            }
          }}
        >
          <InputField
            className="w-full"
            id="edit-driver-name"
            label="Full Name"
            onChange={(value) => setForm({ ...form, name: value })}
            value={form.name}
          />
          <InputField
            className="w-full"
            id="edit-driver-email"
            label="Email"
            onChange={(value) => setForm({ ...form, email: value })}
            type="email"
            value={form.email}
          />
          <InputField
            className="w-full"
            id="edit-driver-phone"
            label="Phone"
            onChange={(value) => setForm({ ...form, phone: value })}
            value={form.phone}
          />
          <InputField
            className="w-full"
            id="edit-driver-address"
            label="Address"
            onChange={(value) => setForm({ ...form, address: value })}
            value={form.address}
          />
          <InputField
            className="w-full"
            id="edit-driver-license"
            label="License Number"
            onChange={(value) => setForm({ ...form, licenseNumber: value })}
            value={form.licenseNumber}
          />
          <InputField
            className="w-full"
            id="edit-driver-license-expiry"
            label="License Expiry"
            onChange={(value) => setForm({ ...form, licenseExpiry: value })}
            type="date"
            value={form.licenseExpiry}
          />
          <div className="relative md:col-span-2">
            <select
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              disabled={loadingVehicles}
              id="edit-driver-vehicle"
              onChange={(e) => setForm({ ...form, vehicle: e.target.value })}
              value={form.vehicle}
            >
              <option value="">
                {loadingVehicles ? "Loading vehicles..." : "Select Vehicle"}
              </option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.brand} {v.model} ({v.plate})
                </option>
              ))}
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="edit-driver-vehicle"
            >
              Assigned Vehicle
            </label>
          </div>
          <div className="relative md:col-span-2">
            <select
              className="peer w-full rounded border border-gray-300 bg-transparent px-3 py-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              id="edit-driver-status"
              onChange={(e) =>
                setForm({
                  ...form,
                  status: e.target.value as DriverFormData["status"],
                })
              }
              required
              value={form.status}
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
            <label
              className="pointer-events-none absolute left-3 top-2 z-10 origin-[0] -translate-y-4 scale-75 transform bg-white px-1 text-gray-500 duration-200 peer-focus:-translate-y-4 peer-focus:scale-75 dark:bg-gray-800 dark:text-gray-400"
              htmlFor="edit-driver-status"
            >
              Status
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2 md:col-span-2">
            <button
              className="rounded bg-gray-200 px-4 py-2 text-gray-700 dark:bg-gray-700 dark:text-gray-200"
              onClick={() => {
                toast("Edit cancelled");
                onClose();
              }}
              type="button"
            >
              Cancel
            </button>
            <button
              className="rounded bg-blue-600 px-4 py-2 text-white dark:bg-blue-500"
              type="submit"
            >
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDriverModal;
